/**
 * Looking round while locked (user 10-11): with a target locked (systems/duelLock.ts), holding the right mouse button
 * turns the character to face the way the camera looks instead of the target; letting go gives the lock's facing back.
 * The client sends `lockOn` `look` with the camera's forward direction on the map (0, 0 when the button comes up); it is
 * swallowed here, and each tick, after the lock has turned the player toward its target, the facing is set to the look.
 * Only the facing changes: the target, the aim of every move and the lock itself stay. No lock: nothing happens.
 */
import type { PlayerCommand } from '../world';
import { duelLockOf } from './duelLock';
import type { SystemHost, WorldSystem } from './types';

const SYSTEMS = new WeakMap<object, LockLookSystem>();
export const lockLookOf = (w: object): LockLookSystem | null => SYSTEMS.get(w) ?? null;

export class LockLookSystem implements WorldSystem {
  readonly name = 'lockLook';
  /** Player entity id -> the direction looked (map units, normalised). */
  private readonly looks = new Map<number, { dx: number; dy: number }>();

  constructor(private readonly h: SystemHost) {
    SYSTEMS.set(h.world, this);
  }

  /** The direction a player is looking round in while locked, or null (facing the target as usual). */
  lookOf(id: number): { dx: number; dy: number } | null {
    const l = this.looks.get(id);
    return l && duelLockOf(this.h.world)?.lockedOf(id) != null ? l : null;
  }

  refuse(cmd: PlayerCommand): boolean {
    if (cmd.t !== 'lockOn' || cmd.op !== 'look') return false;
    const id = this.h.world.player.id;
    const l = Math.hypot(cmd.dx, cmd.dy);
    if (!Number.isFinite(l) || l < 1e-6) this.looks.delete(id);
    else this.looks.set(id, { dx: cmd.dx / l, dy: cmd.dy / l });
    return true;
  }

  step(): void {
    const w = this.h.world;
    for (const [id, l] of this.looks) {
      const p = w.get(id);
      if (!p || p.dead) {
        this.looks.delete(id);
        continue;
      }
      if (duelLockOf(w)?.lockedOf(id) == null) continue;
      this.h.face(p, { x: p.x + l.dx, y: p.y + l.dy });
    }
  }
}
